'use client';

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import RelatedBlogCarousel from '@/components/RelatedBlogCarousel';

export default function BlogPost() {
  const [particles, setParticles] = useState<Array<{ x: number; y: number }>>([]);

  useEffect(() => {
    // Initialize particles only on client side
    setParticles(
      [...Array(50)].map(() => ({
        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
        y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000)
      }))
    );
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
        {/* Hero Section */}
        <section className="relative h-[60vh] flex items-center justify-center overflow-hidden -mt-4">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
          <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:40px_40px] opacity-[0.04]"></div>
          <div className="absolute inset-0 bg-[url('/assets/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
          
          {/* Animated glowing orbs */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-primary-500/30 to-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
          </div>
  
          {/* Binary data particles */}
          <div className="absolute inset-0 overflow-hidden">
            {particles.map((particle, i) => (
              <motion.div
                key={i}
                className="absolute text-white/10 font-mono text-sm"
                initial={{
                  x: particle.x,
                  y: particle.y,
                  opacity: 0
                }}
                animate={{
                  y: [null, -100],
                  opacity: [0, 1, 0],
                  x: [null, particle.x + (Math.random() * 100 - 50)]
                }}
                transition={{
                  duration: Math.random() * 10 + 10,
                  repeat: Infinity,
                  delay: Math.random() * 5,
                  ease: "linear"
                }}
              >
                {Math.random() > 0.5 ? '1' : '0'}
              </motion.div>
            ))}
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center">
              <motion.h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                10 Questions to Ask Before <span className="text-transparent bg-clip-text bg-gradient-rainbow">Choosing a Hosting Service</span>
              </motion.h1>
              <motion.div 
                className="text-xl text-slate-300 max-w-3xl mx-auto flex items-center justify-center gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <p>By 1 Dollar Cloud | Published On: November 24, 2024 | Categories: Web Hosting</p>
              </motion.div>
            </div>
          </div>
        </section>
  
        {/* Featured Image */}
        <section className="relative bg-slate-900 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="relative rounded-2xl overflow-hidden shadow-2xl"
            >
              <img
                src="/assets/blog-images/hosting-questions.svg"
                alt="Hosting Questions Illustration"
                className="w-full h-auto"
              />
            </motion.div>
          </div>
        </section>

        {/* Blog Content */}
        <section className="py-20 relative overflow-hidden bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="prose prose-invert prose-lg max-w-none"
            >
              <div className="bg-slate-800/50 rounded-xl p-8 mb-8">
                <p className="text-slate-300">Choosing the right hosting service for your website is a pivotal decision that can influence your online presence, performance, and growth. At <strong>1 Dollar Cloud</strong>, we understand the challenges faced by website owners when selecting a hosting provider. To guide you through this process, we've compiled a list of ten essential questions to ask before making your decision. As you read through these questions, you'll discover why <strong>1 Dollar Cloud</strong> is the perfect choice for your hosting needs.</p>
              </div>
  
              {[
                {
                  title: "1. What Type of Hosting Do You Offer?",
                  content: [
                    "**Why It Matters:** Different websites have varying needs based on traffic, content type, and growth potential. Understanding the types of hosting available helps you choose the right fit.",
                    "**At 1 Dollar Cloud:** We offer a variety of hosting solutions, including shared hosting for beginners, wordpress hosting for optimized cms services, VPS hosting for growing businesses, and dedicated servers for high-traffic websites. Our flexible plans ensure that you can find the perfect match for your needs."
                  ]
                },
                {
                  title: "2. What Is Your Uptime Guarantee?",
                  content: [
                    "**Why It Matters:** Uptime is essential for maintaining your website's availability. A reliable hosting service should offer a solid uptime guarantee.",
                    "**At 1 Dollar Cloud:** We proudly provide a 99.9% uptime guarantee, ensuring that your website remains accessible to visitors around the clock. Our robust infrastructure and proactive monitoring contribute to this reliability."
                  ]
                },
                {
                  title: "3. What Security Features Do You Provide?",
                  content: [
                    "**Why It Matters:** Security is critical for protecting your website from cyber threats. A good hosting provider should offer comprehensive security measures.",
                    "**At 1 Dollar Cloud:** We take security seriously. Our hosting plans come with free SSL certificates, DDoS protection, and regular security updates. Additionally, we perform daily backups to safeguard your data."
                  ]
                },
                {
                  title: "4. How Is Customer Support Structured?",
                  content: [
                    "**Why It Matters:** Having access to responsive customer support can save you time and frustration when issues arise.",
                    "**At 1 Dollar Cloud:** Our dedicated support team is available 24/7 through live chat, email, and phone. We pride ourselves on our quick response times and knowledgeable staff, ensuring that you receive help whenever you need it."
                  ]
                },
                {
                  title: "5. What Are the Pricing Plans and Renewal Rates?",
                  content: [
                    "**Why It Matters:** Understanding the pricing structure helps you avoid unexpected costs in the future.",
                    "**At 1 Dollar Cloud:** We offer transparent pricing with no hidden fees. Our plans start at just $1 per month, making quality hosting accessible to everyone. We believe in fair renewal rates that won't break the bank."
                  ]
                },
                {
                  title: "6. Do You Offer a Money-Back Guarantee?",
                  content: [
                    "**Why It Matters:** A money-back guarantee allows you to test the service without financial risk.",
                    "**At 1 Dollar Cloud:** We offer a 07-days money-back guarantee, giving you peace of mind as you explore our hosting services. If you're not satisfied for any reason, simply request a refund within the guarantee period."
                  ]
                },
                {
                  title: "7. What Is Your Backup Policy?",
                  content: [
                    "**Why It Matters:** Regular backups are essential for data recovery in case of loss or corruption.",
                    "**At 1 Dollar Cloud:** Our hosting plans include automated daily backups, ensuring that your data is always safe. You can easily restore your website to a previous state with just a few clicks."
                  ]
                },
                {
                  title: "8. What Are the Scalability Options?",
                  content: [
                    "**Why It Matters:** As your website grows, you may need to upgrade your hosting plan. Understanding scalability options ensures that your hosting provider can accommodate your growth.",
                    "**At 1 Dollar Cloud:** Our scalable hosting solutions allow you to upgrade your plan seamlessly as your website expands. Whether you need more resources or additional features, we've got you covered."
                  ]
                },
                {
                  title: "9. Do You Provide a Free Domain Name?",
                  content: [
                    "**Why It Matters:** A free domain name can save you money when starting your website.",
                    "**At 1 Dollar Cloud:** We offer a free domain name for the first year with our hosting plans, making it easier for you to establish your online presence without additional costs."
                  ]
                },
                {
                  title: "10. What Additional Features Do You Offer?",
                  content: [
                    "**Why It Matters:** Additional features can enhance your hosting experience and improve your website's performance.",
                    "**At 1 Dollar Cloud:** Our hosting plans come with a range of features, including website builders, one-click app installations, and performance optimization tools. We aim to provide everything you need to succeed online."
                  ]
                }
              ].map((section, index) => (
                <motion.div
                  key={section.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-slate-800/50 rounded-xl p-8 mb-8"
                >
                  <h2 className="text-2xl font-bold text-white mb-6">{section.title}</h2>
                  {section.content.map((paragraph, i) => (
                    <p key={i} className="text-slate-300 mb-4" dangerouslySetInnerHTML={{ __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}></p>
                  ))}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-20 relative overflow-hidden bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="prose prose-invert prose-lg max-w-none"
            >
              <div className="bg-slate-800/50 rounded-xl p-8 mb-8">
                <h2 className="text-2xl font-bold text-white mb-6">Making Your Decision</h2>
                <p className="text-slate-300 mb-4">Choosing a hosting provider is a significant decision that requires careful consideration. By asking these ten questions, you can better evaluate potential hosting services and make an informed choice.</p>
                <p className="text-slate-300 mb-4">At <strong>1 Dollar Cloud</strong>, we're confident in our answers to all these questions. Our commitment to providing reliable, secure, and affordable hosting solutions sets us apart in the industry.</p>
                <p className="text-slate-300">Ready to get started? Explore our hosting plans and discover why thousands of website owners trust <strong>1 Dollar Cloud</strong> for their hosting needs.</p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Related Blog Carousel */}
        <section className="py-8 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <RelatedBlogCarousel currentSlug="10-questions-to-ask-before-choosing-a-hosting-service-for-your-website" />
            
          </div>
        </section>
      </div>
    );
}